import { inject, Injectable } from '@angular/core'
import { FavoriteProfilesApiClient, FavouriteProfilesModel, FormworkId } from '@efp/api'
import { firstValueFrom } from 'rxjs'
import { FavouriteProfile } from '../../../models/favourites'
import { FavouriteDao } from '../favourite.dao'
import { OLD_FAVOURITES_VERSION } from '../../../constants/versions'

@Injectable()
export class FavouriteHttpDao extends FavouriteDao {
  private readonly favouriteApiClient = inject(FavoriteProfilesApiClient)

  private mapModelToFavouriteProfile(model: FavouriteProfilesModel): FavouriteProfile {
    return {
      id: model.id,
      createdAt: new Date(model.createdAt),
      updatedAt: new Date(model.updatedAt),
      name: model.name,
      isStandard: model.isStandard,
      basis: JSON.parse(model.basisJson),
      values: JSON.parse(model.favouritesJson),
      formworkSystemId: model.formworkSystemId,
      useOnlyRentableArticles: model.useOnlyRentableArticles,
      formworkVersion: model.formworkVersion,
      doka360ProjectUid: model.doka360ProjectUid ?? undefined,
    }
  }

  async create(
    favorite: Omit<FavouriteProfile, 'id' | 'createdAt' | 'updatedAt'>
  ): Promise<number> {
    return firstValueFrom(
      this.favouriteApiClient.createFavoriteProfile({
        body: {
          name: favorite.name,
          isStandard: favorite.isStandard,
          favouritesJson: JSON.stringify(favorite.values),
          basisJson: JSON.stringify(favorite.basis),
          formworkSystemId: favorite.formworkSystemId,
          useOnlyRentableArticles: favorite.useOnlyRentableArticles,
          formworkVersion: favorite.formworkVersion ?? OLD_FAVOURITES_VERSION,
          doka360ProjectUid: favorite.doka360ProjectUid,
        },
      })
    )
  }

  async deleteById(id: number): Promise<void> {
    await firstValueFrom(
      this.favouriteApiClient.deleteFavouriteProfile({
        favoriteId: id,
      })
    )
  }

  async findAll(doka360ProjectUid?: string): Promise<FavouriteProfile[]> {
    const favouriteModels = await firstValueFrom(
      this.favouriteApiClient.getAll({ doka360ProjectUid })
    )

    return favouriteModels.map((model) => this.mapModelToFavouriteProfile(model))
  }

  async findAllByFormworkSystemId(
    formworkSystemId: FormworkId,
    doka360ProjectUid?: string
  ): Promise<FavouriteProfile[]> {
    const favouriteModels = await firstValueFrom(
      this.favouriteApiClient.getAll({
        formworkSystemId,
        doka360ProjectUid,
      })
    )

    return favouriteModels.map((model) => this.mapModelToFavouriteProfile(model))
  }

  async findOneById(id: number): Promise<FavouriteProfile | undefined> {
    const favouriteModel = await firstValueFrom(
      this.favouriteApiClient.getFavoriteProfile({
        favoriteId: id,
      })
    )

    return this.mapModelToFavouriteProfile(favouriteModel)
  }

  async findOneStandardByFormworkSystemId(
    formworkSystemId: FormworkId,
    doka360ProjectUid?: string
  ): Promise<FavouriteProfile | undefined> {
    const favouriteModels = await firstValueFrom(
      this.favouriteApiClient.getAll({
        formworkSystemId,
        isStandard: true,
        doka360ProjectUid,
      })
    )

    if (favouriteModels.length === 0) {
      return undefined
    } else {
      return this.mapModelToFavouriteProfile(favouriteModels[0])
    }
  }

  async update(favorite: FavouriteProfile): Promise<number> {
    const updatedProfile = await firstValueFrom(
      this.favouriteApiClient.updateFavoriteProfile({
        favoriteId: favorite.id,
        body: {
          name: favorite.name,
          isStandard: favorite.isStandard,
          favouritesJson: JSON.stringify(favorite.values),
          basisJson: JSON.stringify(favorite.basis),
          formworkSystemId: favorite.formworkSystemId,
          useOnlyRentableArticles: favorite.useOnlyRentableArticles,
          formworkVersion: favorite.formworkVersion ?? OLD_FAVOURITES_VERSION,
        },
      })
    )

    return updatedProfile.id
  }
}
